import {
  Button,
  Dialog,
  Field,
  Input,
  Portal,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { toaster } from '@mono/chakra';
import { isHttpError } from '@mono/http-hook';
import { FC, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

type FormValues = {
  name: string;
  slug?: string;
  description?: string;
};

type CreateProjectModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: FormValues) => Promise<void>;
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (isHttpError(error)) {
    const responseData = error.response?.data;
    if (responseData && typeof responseData === 'object') {
      const { error: errorMessage, message: responseMessage } =
        responseData as { error?: string; message?: string };
      return errorMessage ?? responseMessage ?? error.message ?? fallback;
    }
    return error.message ?? fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

const CreateProjectModal: FC<CreateProjectModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const {
    handleSubmit,
    register,
    reset,
    formState,
  } = useForm<FormValues>({
    defaultValues: {
      name: '',
      slug: '',
      description: '',
    },
  });
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      reset(
        {
          name: '',
          slug: '',
          description: '',
        },
        { keepDirty: false }
      );
      setServerError(null);
    }
  }, [isOpen, reset]);

  const closeModal = () => {
    onClose();
  };

  const handleFormSubmit: SubmitHandler<FormValues> = async (values) => {
    setServerError(null);

    try {
      await onSubmit({
        name: values.name.trim(),
        slug: values.slug?.trim() || undefined,
        description: values.description?.trim() || undefined,
      });

      toaster.create({
        type: 'success',
        title: 'Project created',
        description: 'Your new project is ready to go.',
      });

      closeModal();
    } catch (error) {
      const message = getErrorMessage(error, 'Unable to create project.');
      setServerError(message);
      toaster.create({
        type: 'error',
        title: 'Failed to create project',
        description: message,
      });
    }
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) {
          closeModal();
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.CloseTrigger />
            <Dialog.Header flexDirection="column" gap="1">
              <Dialog.Title>Create project</Dialog.Title>
              <Dialog.Description>
                Launch a new workstream and invite collaborators.
              </Dialog.Description>
            </Dialog.Header>
            <Dialog.Body>
              <form id="create-project-form" onSubmit={handleSubmit(handleFormSubmit)}>
                <Stack gap="4">
                  <Field.Root invalid={Boolean(formState.errors.name)}>
                    <Field.Label>Project name</Field.Label>
                    <Input
                      autoFocus
                      placeholder="Customer Retention Initiative"
                      {...register('name', {
                        required: 'Project name is required.',
                        minLength: {
                          value: 2,
                          message: 'Name must contain at least 2 characters.',
                        },
                      })}
                    />
                    <Field.HelperText>
                      Choose a clear, action-oriented name for your project.
                    </Field.HelperText>
                    <Field.ErrorText>{formState.errors.name?.message}</Field.ErrorText>
                  </Field.Root>

                  <Field.Root invalid={Boolean(formState.errors.slug)}>
                    <Field.Label>Slug (optional)</Field.Label>
                    <Input
                      placeholder="customer-retention"
                      {...register('slug', {
                        pattern: {
                          value: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                          message:
                            'Use lowercase letters, numbers, and hyphens only.',
                        },
                      })}
                    />
                    <Field.HelperText>
                      Leave blank to generate one automatically.
                    </Field.HelperText>
                    <Field.ErrorText>{formState.errors.slug?.message}</Field.ErrorText>
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Description (optional)</Field.Label>
                    <Textarea
                      placeholder="Outline the goals, deliverables, and success measures."
                      rows={4}
                      {...register('description', {
                        maxLength: {
                          value: 500,
                          message: 'Description must be 500 characters or less.',
                        },
                      })}
                    />
                    <Field.HelperText>
                      Provide context so collaborators understand the vision.
                    </Field.HelperText>
                    <Field.ErrorText>
                      {formState.errors.description?.message}
                    </Field.ErrorText>
                  </Field.Root>

                  {serverError ? (
                    <Text color="fg.error" fontSize="sm">
                      {serverError}
                    </Text>
                  ) : null}
                </Stack>
              </form>
            </Dialog.Body>
            <Dialog.Footer gap="3" justifyContent="flex-end">
              <Button
                variant="ghost"
                onClick={closeModal}
                disabled={formState.isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="create-project-form"
                loading={formState.isSubmitting}
                colorPalette="primary"
              >
                Create project
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default CreateProjectModal;
